import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./PhotographersPage.css";

// 备用摄影师数据，仅在API完全失败时使用
const fallbackData = [
  {
    id: 172,
    name: "Sandy Skoglund",
    years: "1946",
    nationality: "U.S.",
  },
  {
    id: 173,
    name: "Sarah Moon",
    years: "1941",
    nationality: "France",
  },
];

// 清理姓名函数：去除重音符号、前导符号，转大写
function cleanName(name = "") {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/^[^A-Za-z0-9]+/, "")
    .trim()
    .toUpperCase();
}

function mapFallbackNationality(nationality, language) {
  if (language !== "CN") return nationality;
  if (nationality === "U.S.") return "美国";
  if (nationality === "France") return "法国";
  return nationality;
}

function PhotographersPage({ language = "EN" }) {
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [animationReady, setAnimationReady] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState(null);

  // 使用随网站部署的静态 JSON 加载摄影师数据
  useEffect(() => {
    const fetchPhotographersData = async () => {
      setLoading(true);
      try {
        // 现行实现：直接读取静态JSON（public/data/photographers.json）
        const res = await fetch("/data/photographers.json");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        console.log(
          `✅ Photographers loaded from static JSON (${data?.length || 0} items)`
        );
        if (data && Array.isArray(data) && data.length > 0) {
          // 处理数据，添加sortKey用于准确的字母筛选
          const processedData = data
            .map((photographer) => {
              const years = photographer.birthyear
                ? `${photographer.birthyear}`
                : language === "CN"
                ? "未知"
                : "Unknown";

              return {
                id: photographer.photographerid,
                name: photographer.name,
                years,
                nationality:
                  language === "CN"
                    ? photographer.nation_zh || photographer.nation || "未知"
                    : photographer.nation || "Unknown",
                sortKey: cleanName(photographer.name), // 添加清理后的排序键
              };
            })
            .sort((a, b) => a.sortKey.localeCompare(b.sortKey)); // 按清理后的名字排序

          console.log("Processed data length:", processedData.length);
          setPhotographers(processedData);
          setError(null);
        } else {
          console.log("No data received or empty array");
          setError(
            language === "CN"
              ? "未收到摄影师数据"
              : "No photographers data received"
          );
          // 为备用数据也添加sortKey
          const processedFallback = fallbackData.map((photographer) => ({
            ...photographer,
            nationality: mapFallbackNationality(photographer.nationality, language),
            sortKey: cleanName(photographer.name),
          }));
          setPhotographers(processedFallback);
        }
      } catch (error) {
        console.error("Error loading static photographers JSON:", error);
        setError(
          language === "CN"
            ? `加载摄影师数据失败：${error.message}`
            : `Failed to load photographers: ${error.message}`
        );
        // 为备用数据也添加sortKey
        const processedFallback = fallbackData.map((photographer) => ({
          ...photographer,
          nationality: mapFallbackNationality(photographer.nationality, language),
          sortKey: cleanName(photographer.name),
        }));
        setPhotographers(processedFallback);
      } finally {
        setLoading(false);
        // 数据加载完成后，延迟一小段时间再触发动画
        setTimeout(() => {
          setAnimationReady(true);
        }, 100);
      }
    };

    fetchPhotographersData();
  }, [language]);

  // 使用更准确的字母筛选：基于清理后的sortKey
  const filteredPhotographers = photographers.filter((photographer) => {
    return (
      selectedLetter === null ||
      (photographer.sortKey && photographer.sortKey.startsWith(selectedLetter))
    );
  });

  // 字母筛选时重置动画状态，使新的结果也有动画效果
  useEffect(() => {
    if (!loading) {
      setAnimationReady(false);
      setTimeout(() => {
        setAnimationReady(true);
      }, 50);
    }
  }, [selectedLetter, loading]);

  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

  return (
    <div className="photographers-page">
      {/* 页面标题 */}
      <div className="photographers-header">
        <h1>{language === "CN" ? "摄影师" : "PHOTOGRAPHERS"}</h1>
        <p className="intro-text">
          {language === "CN"
            ? "这里汇集了对摄影史产生深远影响的摄影师。他们以创新的技法、独特的视角与开创性的作品，共同塑造了摄影的发展。"
            : "This is a collection of influential photographers who have shaped the history of photography through their innovative techniques, unique perspectives, and groundbreaking work."}
        </p>
      </div>

      {/* 字母筛选 */}
      <div className="alphabet-filter">
        {alphabet.map((letter) => (
          <span
            key={letter}
            className={`filter-letter ${
              selectedLetter === letter ? "active" : ""
            }`}
            onClick={() => {
              // 点击相同字母则取消筛选，点击不同字母则切换筛选
              setSelectedLetter((prev) => (prev === letter ? null : letter));
            }}
          >
            {letter}
          </span>
        ))}
      </div>

      {/* 数据状态信息 */}
      <div className="data-status">
        {error && <p className="error-message">Notice: {error}</p>}
      </div>

      {/* 加载状态 */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-animation">
            <div className="loading-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
            <p className="loading-text">
              {language === "CN"
                ? "正在加载摄影师数据..."
                : "Loading photographers..."}
            </p>
          </div>
        </div>
      ) : (
        /* MOMA风格摄影师网格 */
        <div
          className={`moma-photographers-grid ${
            animationReady ? "animation-ready" : ""
          }`}
          key={selectedLetter || "ALL"} // 强制重新挂载，彻底清空旧节点
        >
          {filteredPhotographers.length > 0 ? (
            filteredPhotographers.map((photographer, index) => (
              <Link
                to={`/photographers/${photographer.id}`}
                className="moma-photographer-link"
                key={photographer.id || photographer.name}
              >
                <div
                  className="moma-photographer-item entry-animate"
                  style={{
                    animationDelay:
                      animationReady ? `${index * 0.018}s` : "0s",
                  }}
                >
                  <div className="photographer-hover-box">
                    <h3 className="photographer-name">{photographer.name}</h3>
                    <div className="photographer-details">
                      <p className="photographer-nationality">
                        {photographer.nationality}
                      </p>
                      {photographer.years && (
                        <p className="photographer-years">
                          {photographer.years}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))
          ) : (
            <div className="no-results">
              <div className="no-results-content">
                <div className="no-results-icon">—</div>
                <p className="no-results-text">
                  {language === "CN" ? "未找到结果" : "No results found"}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PhotographersPage;
